(ns frontend.view.settings2.surveys.survey-block-editor
  (:require
   [common.localization :as loc]
   [ui.components.reorder-controls.view :as reorder-controls]
   [common.mandatory :as mandatory]
   [common.util :as cu]
   [common.visibility :as visibility]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.nev.surveys.form.components :as components]
   [frontend.view.settings2.surveys.components :as survey-components]
   [frontend.view.settings2.surveys.events :as events]
   [frontend.view.settings2.surveys.sub :as sub]
   [frontend.view.settings2.surveys.timeseries.view :as timeseries]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.ck-editor.view :as ck-editor]
   [ui.components.modal.style :as modal-style]
   [ui.components.modal.view :as modal]
   [ui.components.query-builder.view :as query-builder]
   [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
   [ui.components.radix-ui.popover.view :as popover]
   [ui.components.radix-ui.tooltip.view :as tooltip]
   [ui.components.select.select :as select]
   [ui.editors.data-input :as data-input]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.util :as uu]))

(defn add-block-select
  []
  [select/view
   {:value       nil
    :on-change   #(rf/dispatch [::events/add-block %])
    :placeholder (loc/tr :add-survey-block)
    :options     [{:value :survey-block/wysiwyg
                   :label (loc/tr :survey-block/wysiwyg)}
                  {:value :survey-block/section
                   :label (loc/tr :survey-block/section)}
                  {:value :survey-block/table-block
                   :label (loc/tr :survey-block/table-block)}]}])


(defn add-content-item-select
  [db-path]
  [:div
   {:class "flex flex-row gap-4"}
   [button/sm-primary-solid
    {:on-click (fn [_]
                 (rf/dispatch [::events/add-survey-item db-path false :survey-item/question]))}
    (loc/tr :add-survey-item-question)]
   [button/sm-primary-solid
    {:on-click (fn [_]
                 (rf/dispatch [::events/add-survey-item db-path false :survey-item/wysiwyg]))}
    (loc/tr :add-survey-item-wysiwyg)]])

(defn mandatoriness-select
  [{:keys [db-path] :as question}]
  (let [visibility (:visibility question)]
    (when-not (= visibility :visibility/hidden)
      [:div
       [labelled-input/view
        {:label       (loc/tr :survey-question-mandatoriness-config)
         :editor-type :editor.type/select
         :value       {:value (:mandatoriness question)
                       :label (loc/tr (:mandatoriness question))}
         :on-change   #(rf/dispatch [::events/set (concat db-path [:mandatoriness]) %])
         :placeholder (loc/tr :select-mandatoriness-level)
         :options     (remove nil?
                              [(if (some #{visibility} [:visibility/conditional])
                                 {:value :mandatory/when-visible
                                  :label (loc/tr :mandatory/when-visible)}
                                 {:value :mandatory/always
                                  :label (loc/tr :mandatory/always)})
                               (if (some #{visibility} [:visibility/conditional])
                                 {:value :mandatory/conditional-when-visible
                                  :label (loc/tr :mandatory/conditional-when-visible)}
                                 {:value :mandatory/conditional
                                  :label (loc/tr :mandatory/conditional)})
                               {:value :mandatory/optional
                                :label (loc/tr :mandatory/optional)}])}]])))

(defn skip-select
  [{:keys [db-path] :as question}]
  (let [visibility (:visibility question)]
    (when (and (not= visibility :visibility/hidden)
               (session/has-feature-access? :feature/survey-skip-responses))
      [:div
       [select/view
        {:id        (str "survey-question-skip-" db-path)
         :value     (:cannot-skip? question)
         :label     (if (:cannot-skip? question)
                      (loc/tr :cannot-skip?)
                      (loc/tr :can-skip?))
         :on-change #(rf/dispatch [::events/set (concat db-path [:cannot-skip?]) %])
         :options   [{:value false
                      :label (loc/tr :can-skip?)}
                     {:value true
                      :label (loc/tr :cannot-skip?)}]}]])))

(defn visibility-select
  [{:keys [db-path] :as question}]
  (let [visibility (or (:visibility question)
                       :visibility/always)]
    [labelled-input/view
     {:label       (loc/tr :survey-question-visibility-configuration)
      :editor-type :editor.type/select
      :value       {:value visibility
                    :label (loc/tr visibility)}
      :on-change   (fn [e]
                     (rf/dispatch [::events/set (concat db-path [:visibility]) e]))
      :placeholder (loc/tr :select-visibility-level)
      :options     [{:value :visibility/always
                     :label (loc/tr :visibility/always)}
                    {:value :visibility/conditional
                     :label (loc/tr :visibility/conditional)}
                    {:value :visibility/hidden
                     :label (loc/tr :visibility/hidden)}]}]))

(declare survey-item-tools visibility-icon visibility-condition-popover mandatoriness-icon info-icon)

(defn survey-item-wysiwyg-visibility-controls
  []
  (let [local-state (r/atom {})]
    (fn [item]
      (let [disabled-item (assoc item
                                 :mandatoriness :mandatory/optional
                                 :localized-info nil)]
      [:<>
       [visibility-icon local-state item]
       [:div
        {:class "pointer-events-none opacity-40"}
        [mandatoriness-icon local-state disabled-item]]
       [:div
        {:class "pointer-events-none opacity-40"}
        [info-icon local-state disabled-item]]
       [visibility-condition-popover local-state item]]))))

(defn survey-item-wysiwyg
  [{:keys [lang db-path] :as item}]
  (let [item (assoc item :visibility (or (:visibility item)
                                         :visibility/always))]
    [:div
     {:class "flex flex-col gap-3"}
    [:div
      {:class "flex items-center justify-end gap-1"}
      [:div
       {:class "ml-4 flex items-center gap-1"}
       [survey-item-wysiwyg-visibility-controls item]]
      [survey-item-tools item]]
     [data-input/view
      {:data-type     :data.type/text-richtext
       :focus-border? true
       :border?       true
       :value         (:localized-content item)
       :on-blur       #(rf/dispatch [::events/set (concat db-path [:content lang]) %])}]]))

(defn wysiwyg-block
  [{:keys [lang db-path] :as block}]
  [ck-editor/editor
   {:value     (:localized-content block)
    :on-change (fn [v]
                 (rf/dispatch [::events/set (concat db-path [:content lang]) v]))}])

(defn survey-item-tools
  [{:keys [db-path first? last?]}]
  [reorder-controls/view
   {:first?         first?
    :last?          last?
    :remove-label   (loc/tr :survey-block-editor/remove-item)
    :on-move-to-top #(rf/dispatch [::events/move-item-first db-path])
    :on-move-up     #(rf/dispatch [::events/move-item-up db-path])
    :on-move-down   #(rf/dispatch [::events/move-item-down db-path])
    :on-remove      #(rf/dispatch [::events/remove-vec-item db-path])}])

(defn question-label
  [question]
  (if-let [localized-label (:localized-label question)]
    [:span
     {:class "inline-block text-bold"}
     localized-label]
    [:span
     {:class ""}
     (:other-lang-label question)]))

(defn info-text
  [{:keys [lang db-path] :as item}]
  [labelled-input/view
   {:label         (loc/tr :survey-question-info-label)
    :data-type     :data.type/text-richtext
    :value         (:localized-info item)
    :focus-border? true
    :placeholder   (loc/tr :survey-question/info-text-placeholder)
    :on-blur       #(rf/dispatch [::events/set (concat db-path [:survey-question/info-translations lang]) %])}])


(defn survey-item-question-label
  [{:keys [survey-question/data-unit] :as question}]
  [:div
   {:class "flex flex-row"}
   [:label
    {:class "text-semibold flex-1"}
    [question-label question]
    (when data-unit
      [:span
       {:class "ps-1"}
       "(" (:survey-question/data-unit question) ")"])]
   [survey-item-tools question]])

(defn survey-item-question-config
  [{:keys [lang selected-questions-options question-choices query-schema db-path] :as question}]
  (let [mandatoriness (:mandatoriness question)
        visibility (:visibility question)
        hide-skip? @(rf/subscribe [::sub/hide-no-answer?])
        excel-info-use? @(rf/subscribe [::sub/excel-info-show?])]
    [:<>
     [:div
      [visibility-select question]]
     (when (= visibility :visibility/conditional)
       [query-builder/view
        {:db-path          (concat db-path [:visibility-rules])
         :rules            (:visibility-rules question)
         :rule-label       (loc/tr :question)
         :level            0
         :field-options    (->> selected-questions-options
                                (cu/remove-by :id (:db/id question)))
         :question-choices question-choices
         :query-schema     query-schema}])
     [mandatoriness-select question]
     (when (or (and (= :visibility/always visibility)
                    (= :mandatory/conditional mandatoriness))
               (and (= :visibility/conditional visibility)
                    (= :mandatory/conditional-when-visible mandatoriness)))
       [:<>
        [query-builder/view
         {:label            (if (= :visibility/always visibility)
                              (loc/tr :this-question-is-mandatory-when)
                              (loc/tr :this-question-is-mandatory-when-visible-and))
          :db-path          (concat db-path [:mandatoriness-rules])
          :rules            (:mandatoriness-rules question)
          :rule-label       (loc/tr :question)
          :level            0
          :field-options    (->> selected-questions-options
                                 (cu/remove-by :id (:db/id question)))
          :question-choices question-choices
          :query-schema     query-schema}]])

     (when (and (not hide-skip?)
                (some #{mandatoriness} [:mandatory/always :mandatory/when-visible :mandatory/conditional-when-visible]))
       [skip-select question])

     (when (some #{(:survey-question/data-type question)}
                 [:data.type/number
                  :data.type/currency-value
                  :data.type/percentage
                  :data.type/calculated-number
                  :data.type/multiplier])
       [labelled-input/view
        {:data-type :data.type/integer
         :label     (loc/tr :number-precision)
         :value     (:precision question)
         :on-blur   #(rf/dispatch [::events/set (concat db-path [:precision]) %])}])

     (when excel-info-use?
       [:<>
        [:h3
         {:class "pt-2 text-semibold"}
         (loc/tr :excel-visibility-infos)]
        [:ul
         {:class "flex flex-col flex-auto gap-1"}
         (some->> (:visibility-infos question)
                  (seq)
                  (map-indexed (fn [idx {:keys [code label]}]
                                 (let [localized-code (get code lang)
                                       localized-label (get label lang)]
                                   ^{:key (str db-path "-visibility-" idx)}
                                   [:li.flex.flex-row.flex-auto.justify-items-start.gap-1.align-items-center
                                    [:div
                                     [data-input/view
                                      {:data-type :data.type/text-simple
                                       :value     localized-code
                                       :on-blur   (fn [new-value]
                                                    (rf/dispatch [::events/set (concat db-path [:visibility-infos idx :code lang]) new-value]))}]]
                                    [:div
                                     [data-input/view
                                      {:data-type :data.type/text-simple
                                       :value     localized-label
                                       :on-blur   (fn [new-value]
                                                    (rf/dispatch [::events/set (concat db-path [:visibility-infos idx :label lang]) new-value]))}]]
                                    [:i.fal.fa-times-circle.text-danger
                                     {:on-click (fn [_]
                                                  (rf/dispatch [::events/set (concat db-path [:visibility-infos])
                                                                (cu/vec-remove idx (vec (:visibility-infos question)))]))}]]))))
         [:li
          {:class "flex flex-row flex-auto"}
          [button/sm-secondary-hollow
           {:on-click (fn []
                        (rf/dispatch [::events/set (concat db-path [:visibility-infos]) (vec (concat (:visibility-infos question)
                                                                                                     [{:code  nil
                                                                                                       :label nil}]))]))}
           (loc/tr :add-visibility-info)]]]])]))

(defn info-icon
  [local-state question]
  ^{:key (str ::visibility "-" (:survey-question-id question) "-" (:localized-info question) "-" "test")}
  [popover/view
   {:open?          (:info-open? @local-state)
    :align          "center"
    :side           "top"
    :on-open-change (fn [open?]
                      (swap! local-state assoc :info-open? open?))}

   [tooltip/view
    [:i
     {:class (uu/cm "fa-sharp fa-circle-info"
                    (if (cu/blank-value? (:localized-info question))
                      "fa-light"
                      "fa-solid"))}]
    [data-input/formatted-display-string
     {:data-type :data.type/text-richtext
      ;; The guidance text only. This used to append
      ;; `(pr-str (:survey-question/info-translations question))`, so hovering
      ;; a question with guidance showed the reader a printed Clojure map of
      ;; every translation after it.
      :value     (if (cu/blank-value? (:localized-info question))
                   (loc/tr :survey-block-editor/no-guidance-text)
                   (str "<p>" (:localized-info question) "</p>"))}]]
   [:div
    {:class    "p-2 max-w-[320px]"
     :on-click (fn [e]
                 (fu/stop-n-prevent e))}
    [info-text question]]])

(defn visibility-condition-popover
  [local-state {:keys [db-path selected-questions-options question-choices query-schema] :as question}]
  [modal/view
   {:visible? (:visibility-condition-editor-open? @local-state)
    :hide!    (fn [_]
                (swap! local-state assoc :visibility-condition-editor-open? false))
    :z-index  60000
    :size     ::modal-style/lg}
   (loc/tr :question-is-visible-when)
   [query-builder/view
    {:db-path          (concat db-path [:visibility-rules])
     :rules            (:visibility-rules question)
     :rule-label       (loc/tr :question)
     :level            0
     :field-options    (->> selected-questions-options
                            (cu/remove-by :id (:db/id question)))
     :question-choices question-choices
     :query-schema     query-schema}]
   [:div
    {:class "flex flex-row-reverse"}
    [button/primary-solid
     {:on-click (fn []
                  (swap! local-state assoc :visibility-condition-editor-open? false))}
     (loc/tr :close)]]])

(defn mandatoriness-condition-popover
  [local-state {:keys [selected-questions-options question-choices query-schema visibility db-path] :as question}]
  [modal/view
   {:visible? (:mandatory-condition-editor-open? @local-state)
    :hide!    (fn [_]
                (swap! local-state assoc :mandatory-condition-editor-open? false))
    :z-index  60000
    :size     ::modal-style/lg}
   (loc/tr :mandatoriness-conditions)
   [query-builder/view
    {:label            (if (= :visibility/always visibility)
                         (loc/tr :this-question-is-mandatory-when)
                         (loc/tr :this-question-is-mandatory-when-visible-and))
     :db-path          (concat db-path [:mandatoriness-rules])
     :rules            (:mandatoriness-rules question)
     :rule-label       (loc/tr :question)
     :level            0
     :field-options    (->> selected-questions-options
                            (cu/remove-by :id (:db/id question)))
     :question-choices question-choices
     :query-schema     query-schema}]
   [:div
    {:class "flex flex-row-reverse"}
    [button/primary-solid
     {:on-click (fn []
                  (swap! local-state assoc :mandatory-condition-editor-open? false))}
     (loc/tr :close)]]])

(defn visibility-icon
  [local-state {:keys [visibility db-path] :as question}]
  ^{:key (str ::visibility "-" (:survey-question-id question) "-" visibility)}
  [dropdown-menu/view
   {:trigger [:i
              {:title (loc/tr visibility)
               :class (visibility/icon visibility)}]
    :items   (concat (->> [:visibility/always :visibility/hidden :visibility/conditional]
                          (mapv (fn [k]
                                  {:label      (loc/tr k)
                                   :icon-class (visibility/icon k)
                                   :selected?  (= visibility k)
                                   :on-click   (fn [_]
                                                 (when (= k :visibility/conditional)
                                                   (swap! local-state assoc :visibility-condition-editor-open? true))
                                                 (rf/dispatch [::events/set (concat db-path [:visibility]) k]))})))
                     (when (= visibility :visibility/conditional)
                       [{:separator? true}
                        {:custom-item? true
                         :value        [:div
                                        {:class "flex flex-row justify-content-center"}
                                        [:span
                                         {:class "flex"}
                                         (loc/tr :view-edit-condition)]]
                         :on-click     (fn [_]
                                         (swap! local-state assoc :visibility-condition-editor-open? true))}]))}])

(defn mandatoriness-icon
  [local-state {:keys [mandatoriness visibility db-path] :as question}]
  ^{:key (str ::mandatoriness "-" (:survey-question-id question) "-" mandatoriness)}
  [dropdown-menu/view
   {:trigger [:i
              {:title (loc/tr mandatoriness)
               :class (mandatory/icon mandatoriness)}]
    :items   (concat (->> (if (= visibility :visibility/always)
                            [:mandatory/always :mandatory/optional :mandatory/conditional]
                            [:mandatory/when-visible :mandatory/optional :mandatory/conditional-when-visible])
                          (mapv (fn [k]
                                  {:label      (loc/tr k)
                                   :icon-class (mandatory/icon k)
                                   :selected?  (= mandatoriness k)
                                   :on-click   (fn [_]
                                                 (when (some #{k} [:mandatory/conditional :mandatory/conditional-when-visible])
                                                   (swap! local-state assoc :mandatory-condition-editor-open? true))
                                                 (rf/dispatch [::events/set (concat db-path [:mandatoriness]) k]))})))
                     (when (some #{mandatoriness} [:mandatory/conditional :mandatory/conditional-when-visible])
                       [{:separator? true}
                        {:custom-item? true
                         :value        [:div
                                        {:class "flex flex-row justify-content-center"}
                                        [:span
                                         {:class "flex"}
                                         (loc/tr :view-edit-condition)]]
                         :on-click     (fn [_]
                                         (swap! local-state assoc :mandatory-condition-editor-open? true))}]))}])

(defn survey-question-config-icons
  [_question]
  (let [local-state (r/atom {})]
    (fn [question]
      [:div
       {:class "flex items-center justify-end gap-1"}
       [visibility-icon local-state question]
       [mandatoriness-icon local-state question]
       [info-icon local-state question]
       [visibility-condition-popover local-state question]
       [mandatoriness-condition-popover local-state question]])))

(defn survey-question-row
  [{:keys [data-unit] :as question}]
  [:div
   {:class "flex flex-row gap-4"}
   [:label
    {:class "text-semibold flex-1"}
    [question-label question]
    (when data-unit
      [:span
       {:class "ps-1"}
       "(" (:survey-question/data-unit question) ")"])]
   [survey-question-config-icons question]
   [survey-item-tools question]])

(defn survey-item-question
  [question]
  [survey-question-row question]
  #_[:div
     {:class "flex flex-col gap-3"}
     [:div
      {:class "flex flex-col gap-3"}
      [survey-item-question-label question]
      [survey-item-question-config question]]
     [:div
      {:class "flex flex-col gap-3"}
      [info-text question]]])

(defn section-content-item
  [{:keys [survey-item-type] :as props}]
  [:div
   {:class "py-2 px-3 border border-solid border-base-regular-border-paler rounded"}
   (case survey-item-type
     :survey-item/wysiwyg
     [survey-item-wysiwyg props]
     :survey-item/question
     [survey-item-question props]
     (str "Item " (pr-str props)))])

(defn section-content
  [{:keys [lang content selected-questions-options question-choices query-schema db-path]}]
  [:div
   {:class "px-4 flex flex-col gap-3"}
   (into
    [:ul
     {:class "flex flex-col gap-2"}]
    (->> content
         (map-indexed (fn [index item]
                        ^{:key (uu/react-key item)}
                        [section-content-item
                         (assoc item
                           :lang lang
                           :selected-questions-options selected-questions-options
                           :question-choices question-choices
                           :query-schema query-schema
                           :db-path (concat db-path [:content index])
                           :first? (zero? index)
                           :last? (= index (dec (count content))))]))
         (doall)))
   [add-content-item-select (concat db-path [:content])]])

(defn timeseries-block
  [ctx block]
  [:div
   {:class "flex flex-col gap-3"}
   ;[section-heading block]
   [:div
    {:class "flex flex-col gap-4"}
    [timeseries/view ctx block]]])

(defn section-block
  [block]
  [:div
   {:class "flex flex-col gap-3"}
   ;[section-heading block]
   [:div
    {:class "flex flex-col gap-4"}
    (when (:content block)
      [section-content block])
    ;[section-info block]
    ]])

(defn block-header
  [{:keys [first? db-path last? localized-label lang]}]
  [:div
   {:class "flex flex-row gap-2 bg-complement-light"}
   [:div
    {:class "flex flex-1 justify-start p-2"}
    [data-input/view
     {:data-type   :data.type/text-simple
      :value       localized-label
      :full-width? true
      :on-blur     (fn [v]
                     (rf/dispatch [::events/set (concat db-path [:label lang]) v]))}]]
   [reorder-controls/view
    {:first?            first?
     :last?             last?
     :move-to-top-label (loc/tr :survey-block-editor/move-section-to-top)
     :move-up-label     (loc/tr :survey-block-editor/move-section-up)
     :move-down-label   (loc/tr :survey-block-editor/move-section-down)
     :remove-label      (loc/tr :survey-block-editor/remove-section)
     :on-move-to-top    #(rf/dispatch [::events/move-block-first db-path])
     :on-move-up        #(rf/dispatch [::events/move-block-up db-path])
     :on-move-down      #(rf/dispatch [::events/move-block-down db-path])
     :on-remove         #(rf/dispatch [::events/remove-vec-item db-path])}]])

(defn block
  [{:keys [idx block-type db-path] :as block}]
  [components/survey-card
   [:<>
    [block-header block]
    (case block-type
      :survey-block/wysiwyg [wysiwyg-block block]
      :survey-block/section [section-block block]
      :survey-block/table-block [timeseries-block {:block-idx idx
                                                   :ctx-path  db-path}
                                 block]
      (str block-type))]])

(defn editor-body
  []
  (let [supported-languages @(rf/subscribe [::sub/survey-block-languages])
        config @(rf/subscribe [::sub/survey-block-config])
        lang @(rf/subscribe [::sub/selected-language])
        selected-questions-options @(rf/subscribe [::sub/all-question-options])
        all-question-choices @(rf/subscribe [::sub/all-question-choices])
        query-schema @(rf/subscribe [::sub/query-schema])]
    [:div
     {:key   (str "template-editor-" lang)
      :class "p-4 lg:w-5xl mx-auto flex flex-col gap-4"}
     [:div
      {:class "flex flex-col gap-4"}
      [:div
       {:class "text-xl font-bold"}
       (loc/tr :survey-block-configuration)]
      [survey-components/lang-selector lang supported-languages]]
     (->> config
          :blocks
          (map-indexed (fn [idx block-item]
                         ^{:key (uu/react-key block-item)}
                         [block
                          (assoc block-item
                                 :selected-questions-options selected-questions-options
                                 :question-choices all-question-choices
                                 :query-schema query-schema
                                 :lang lang
                                 :first? (zero? idx)
                                 :last? (= idx (dec (count (:blocks config))))
                                 :db-path [::sub/config-draft :blocks idx]
                                 :idx idx)]))
          (doall))
     [add-block-select]]))

(defn editor-footer
  []
  (let [changes? @(rf/subscribe [::sub/block-draft-changes?])
        missing-language-versions? @(rf/subscribe [::sub/block-missing-lang-versions?])]
    [:div
     {:class "flex mx-auto w-5xl justify-between"}
     [:div
      {:class "flex gap-3"}
      (if changes?
        [button/secondary-hollow
         {:on-click #(rf/dispatch [::events/close-survey-block-editor])}
         (loc/tr :close-without-saving)]
        [button/secondary-hollow
         {:on-click #(rf/dispatch [::events/close-survey-block-editor])}
         (loc/tr :close)])]
     [:div
      [button/primary-solid
       {:disabled? (not changes?)
        :on-click  #(rf/dispatch [::events/save-survey-block])
        :tooltip   (when (and (not changes?)
                              missing-language-versions?)
                     (loc/tr :missing-language-versions))}
       (loc/tr :save)]]]))

(defn view
  []
  [modal/view
   {:visible? true
    :hide!    #(rf/dispatch [::events/close-survey-block-editor])
    :z-index  60000
    :size     ::modal-style/full}
   nil
   [editor-body]
   [editor-footer]])
